// Format-change cleanup partition (owner 2026-10-09 bug fix).
//
// Changing a division's format (or reconfiguring pools / round-robin)
// invalidates the generated schedule. The OLD behavior merely nulled each
// game's competitionMeta — but Auto-Populate's "replace" step only recognizes
// games that STILL carry competitionMeta, so those just-cleared games became
// invisible orphans and every subsequent Auto-Populate piled on a fresh set
// (the reported "32 unassigned games" bug).
//
// Fix: a game touched by the generator (competitionMeta / playoffRound /
// placeholder) is disposable when it has NO score — delete it outright instead
// of orphaning it. Games WITH a score are real results: keep them, only
// stripping their schedule wiring. Games the generator never touched
// (manually-created, competitionMeta null, no placeholder) are left entirely
// alone.

export interface CleanupGame {
  _id?: any;
  competitionMeta?: any;
  playoffRound?: any;
  homeTeamPlaceholder?: string;
  awayTeamPlaceholder?: string;
  homeScore?: number | null;
  awayScore?: number | null;
  [key: string]: any;
}

/** A game the schedule generator stamped (so a format change should clear/remove it). */
export function isScheduleAssigned(g: CleanupGame): boolean {
  return !!(g && (g.competitionMeta != null || g.playoffRound || g.homeTeamPlaceholder || g.awayTeamPlaceholder));
}

export function isScoreless(g: CleanupGame): boolean {
  return g.homeScore == null && g.awayScore == null;
}

export interface FormatChangePartition<T extends CleanupGame = CleanupGame> {
  /** Scoreless generator-stamped games — delete outright (they'd otherwise orphan). */
  toDelete: T[];
  /** Scored generator-stamped games — keep, but strip schedule wiring. */
  toClear: T[];
}

export function partitionFormatChangeGames<T extends CleanupGame>(games: T[]): FormatChangePartition<T> {
  const assigned = (games || []).filter(isScheduleAssigned);
  return {
    toDelete: assigned.filter(isScoreless),
    toClear: assigned.filter((g) => !isScoreless(g)),
  };
}
